import { useState } from "react"
import { Link } from "@tanstack/react-router"
import {
  COMPANY_SIZES,
  COMPANY_STAGES,
  companyRequiredProfileMissingLabels,
} from "@/lib/company"
import { usePendingAction } from "@/hooks/use-pending-action"
import { usePrefillOnce } from "@/hooks/use-prefill-once"
import { Skeleton } from "@/components/ui/skeleton"
import { useCompanySettingsData } from "../data/use-company-settings"
import {
  imageFileValidator,
  useStorageUpload,
} from "@/hooks/use-storage-upload"
import { HiringRegionPicker } from "@/components/hiring-region-picker"
import { ImageUploadControl } from "@/components/image-upload-control"
import {
  FormInput,
  FormSelect,
  FormTextarea,
} from "@/components/form/form-field"
import { COMPANY_LIMITS } from "../../../../convex/lib/fieldLimits"
import { toast } from "sonner"
import { capturePostHogEvent } from "@/lib/posthog"

const MAX_LOGO_BYTES = 5 * 1024 * 1024

type CompanySettings = ReturnType<typeof useCompanySettingsData>
type LoadedCompany = Exclude<CompanySettings["company"], undefined>

export function EmployerCompanyForm() {
  const data = useCompanySettingsData()
  if (data.company === undefined) {
    return <Skeleton className="h-48 w-full" />
  }
  return <CompanyProfileFields {...data} company={data.company} />
}

function CompanyProfileFields({
  company,
  saveCompanyProfile,
  setLogo,
}: Omit<CompanySettings, "company"> & { company: LoadedCompany }) {
  const [companyName, setCompanyName] = useState(company?.name ?? "")
  const [companyWebsite, setCompanyWebsite] = useState(company?.website ?? "")
  const [companyLocation, setCompanyLocation] = useState(company?.location ?? "")
  const [companyOneLiner, setCompanyOneLiner] = useState(company?.oneLiner ?? "")
  const [companyAbout, setCompanyAbout] = useState(company?.about ?? "")
  const [companySize, setCompanySize] = useState(company?.size ?? "")
  const [companyStage, setCompanyStage] = useState(company?.stage ?? "")
  const [companyLinkedin, setCompanyLinkedin] = useState(company?.linkedin ?? "")
  const [companyTwitter, setCompanyTwitter] = useState(company?.twitter ?? "")
  const [companyInstagram, setCompanyInstagram] = useState(company?.instagram ?? "")
  const { pending: saving, run: runSave } = usePendingAction()

  const pendingRequirements = companyRequiredProfileMissingLabels({
    name: company?.name,
    website: company?.website,
    linkedin: company?.linkedin,
  })

  const prefillKey =
    company == null
      ? undefined
      : [
          company.name ?? "",
          company.website ?? "",
          company.location ?? "",
          company.oneLiner ?? "",
          company.about ?? "",
          company.size ?? "",
          company.stage ?? "",
          company.linkedin ?? "",
          company.twitter ?? "",
          company.instagram ?? "",
        ].join("\0")

  usePrefillOnce(
    company,
    (canonicalCompany) => {
      setCompanyName(canonicalCompany.name ?? "")
      setCompanyWebsite(canonicalCompany.website ?? "")
      setCompanyLocation(canonicalCompany.location ?? "")
      setCompanyOneLiner(canonicalCompany.oneLiner ?? "")
      setCompanyAbout(canonicalCompany.about ?? "")
      setCompanySize(canonicalCompany.size ?? "")
      setCompanyStage(canonicalCompany.stage ?? "")
      setCompanyLinkedin(canonicalCompany.linkedin ?? "")
      setCompanyTwitter(canonicalCompany.twitter ?? "")
      setCompanyInstagram(canonicalCompany.instagram ?? "")
    },
    prefillKey,
  )

  const { uploading: uploadingLogo, upload: uploadLogo } = useStorageUpload({
    purpose: "company_logo",
    validate: imageFileValidator(MAX_LOGO_BYTES),
    onUploaded: (ref) => setLogo(ref),
    successMessage: "Company logo updated",
  })

  const logoLabel = companyName.trim() || company?.name || "C"

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault()
        void runSave(
          async () => {
            await saveCompanyProfile({
              companyName,
              companyWebsite,
              companyLocation,
              companyOneLiner,
              companyAbout,
              companySize,
              companyStage,
              companyLinkedin,
              companyTwitter,
              companyInstagram,
            })
            capturePostHogEvent("company_profile_saved")
          },
          { success: "Company profile saved" },
        )
      }}
    >
      {company?.verificationStatus === "approved" && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/companies/$companyId"
            params={{ companyId: company._id }}
            className="home-wf-btn home-wf-btn--outline ml-auto"
          >
            View public page
          </Link>
        </div>
      )}

      {company?.verificationStatus !== "approved" && (
        <div className="mkt-panel mkt-panel--wash">
          <p className="mkt-panel__title">
            {company?.verificationStatus === "rejected"
              ? "Verification declined"
              : "Approval pending"}
          </p>
          {pendingRequirements.length > 0 ? (
            <>
              <p className="mkt-panel__copy">
                Still needed before we can review your company:
              </p>
              <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm font-medium leading-5 text-[color:var(--wf-mute)]">
                {pendingRequirements.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          ) : (
            <p className="mkt-panel__copy">
              {company?.verificationStatus === "rejected"
                ? "We couldn't verify your company. Update your details and contact us to re-review."
                : "Required details are in. We'll email you as soon as you're approved."}
            </p>
          )}
        </div>
      )}

      <div className="mkt-panel mkt-form">
        <p className="mkt-panel__title">Basics</p>
        <p className="mkt-panel__copy">
          Shown on your job and hackathon listings. Posting requires this
          website to match your work email.
        </p>
        <div className="mt-4 space-y-4">
          <ImageUploadControl
            label="Company logo"
            hint="Square logos work best · shown on your public page and job cards"
            imageUrl={company?.logoFileUrl}
            uploading={uploadingLogo}
            onUpload={uploadLogo}
            onRemove={async () => {
              await setLogo({})
              toast.success("Company logo removed")
            }}
            fallback={
              <span className="text-2xl font-extrabold text-[color:var(--wf-ink)]">
                {logoLabel.charAt(0).toUpperCase()}
              </span>
            }
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <FormInput
              id="company"
              label="Company name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Acme Labs"
              required
            />
            <FormInput
              id="co-website"
              label="Website"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
              placeholder="https://acme.dev"
              required
            />
            <HiringRegionPicker
              variant="single"
              control="select"
              label="Location"
              value={companyLocation}
              onChange={setCompanyLocation}
            />
          </div>
        </div>
      </div>

      <div className="mkt-panel mkt-form">
        <p className="mkt-panel__title">About</p>
        <p className="mkt-panel__copy">
          Appears on your public company page and everywhere candidates see your jobs.
        </p>
        <div className="mt-4 space-y-4">
          <FormInput
            id="co-oneliner"
            label="One-liner"
            value={companyOneLiner}
            onChange={(e) => setCompanyOneLiner(e.target.value)}
            placeholder="Payments infrastructure for D2C brands"
            limit={COMPANY_LIMITS.oneLiner}
          />
          <FormTextarea
            id="about"
            label="About the company"
            value={companyAbout}
            onChange={(e) => setCompanyAbout(e.target.value)}
            rows={5}
            limit={COMPANY_LIMITS.about}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <FormSelect
              label="Team size"
              value={companySize}
              onValueChange={setCompanySize}
              placeholder="Select size"
              options={COMPANY_SIZES.map((size) => ({
                value: size,
                label: size,
              }))}
            />
            <FormSelect
              label="Stage"
              value={companyStage}
              onValueChange={setCompanyStage}
              placeholder="Select stage"
              options={COMPANY_STAGES.map((stage) => ({
                value: stage,
                label: stage,
              }))}
            />
          </div>
        </div>
      </div>

      <div className="mkt-panel mkt-form">
        <p className="mkt-panel__title">Socials</p>
        <p className="mkt-panel__copy">
          LinkedIn is required so we can verify you. X and Instagram are optional.
        </p>
        <div className="mt-4 space-y-4">
          <FormInput
            id="co-linkedin"
            label="LinkedIn"
            value={companyLinkedin}
            onChange={(e) => setCompanyLinkedin(e.target.value)}
            placeholder="linkedin.com/company/acme"
            required
          />
          <FormInput
            id="co-twitter"
            label="X (Twitter)"
            value={companyTwitter}
            onChange={(e) => setCompanyTwitter(e.target.value)}
            placeholder="x.com/acme"
          />
          <FormInput
            id="co-instagram"
            label="Instagram"
            value={companyInstagram}
            onChange={(e) => setCompanyInstagram(e.target.value)}
            placeholder="instagram.com/acme"
          />
        </div>
      </div>

      <button type="submit" className="home-wf-btn home-wf-btn--lime" disabled={saving}>
        {saving ? "Saving…" : "Save"}
      </button>
    </form>
  )
}
